import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { previewEscrowAnalysis, runEscrowAnalysis } from 'services'
import { Input2, Modal, Select2 } from 'stories/components'
import { formatDate, thousandSeperatorWithPrefix } from 'utils'

/**
 * Run an escrow analysis. The settings a servicer is entitled to choose are asked here rather than
 * fixed in code, pre-filled with values that comply with Regulation X — the cushion is capped at
 * two months (one sixth of annual disbursements) whatever is typed.
 *
 * The result previews as the settings change, so the effect on the borrower's payment is visible
 * before anything is committed.
 */
export const EscrowAnalysisDialog = ({
  defaultStart,
  currentMonthlyEscrow,
  escrowBalance,
  onClose,
  onSaved,
}: {
  defaultStart: string
  currentMonthlyEscrow: number
  escrowBalance: number
  onClose: () => void
  onSaved: () => void
}) => {
  const [params, setParams] = useState<Record<string, any>>({
    computationYearStart: defaultStart,
    cushionMonths: 2,
    shortageMonths: 12,
    deficiencyMonths: 12,
    surplusRefundThreshold: 50,
    startingBalance: escrowBalance,
    currentMonthlyEscrow,
  })
  const [result, setResult] = useState<any>(null)
  const [busy, setBusy] = useState(false)

  const paramsKey = JSON.stringify(params)

  const preview = useCallback(async () => {
    setBusy(true)
    try {
      const res: any = await previewEscrowAnalysis(params)
      setResult(res?.success ? res : null)
    } catch {
      setResult(null)
    } finally {
      setBusy(false)
    }
    // Keyed on the values, not the object identity: the preview should follow what was typed.
  }, [paramsKey])

  useEffect(() => {
    preview()
  }, [preview])

  const set = (key: string, value: any) => setParams((p) => ({ ...p, [key]: value }))

  const onOk = async () => {
    setBusy(true)
    try {
      const res: any = await runEscrowAnalysis(params)
      if (res?.success) {
        toast('Escrow analysis saved.', { type: 'success' })
        onSaved()
        onClose()
      } else {
        toast(res?.message || 'Could not run the analysis.', { type: 'error' })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Run Escrow Analysis"
      titleOkay="Run and Save"
      isOpen
      loading={busy}
      onClose={onClose}
      onOk={onOk}
    >
      <div className="w-[46rem] text-[14px]">
        <div className="grid grid-cols-3 gap-4 mt-2">
          <Input2
            title="Computation Year Starts"
            type="date"
            value={params.computationYearStart}
            onChange={(v: any) => set('computationYearStart', v)}
          />
          <Input2
            title="Current Escrow Balance"
            type="thousandSep"
            prefix="$"
            value={String(params.startingBalance ?? '')}
            onChange={(v: any) => set('startingBalance', v)}
          />
          <Input2
            title="Current Monthly Escrow"
            type="thousandSep"
            prefix="$"
            value={String(params.currentMonthlyEscrow ?? '')}
            onChange={(v: any) => set('currentMonthlyEscrow', v)}
          />
          <Select2
            id="cushion-months"
            title="Cushion"
            options={{ 0: 'None', 1: '1 month', 2: '2 months (maximum)' }}
            value={String(params.cushionMonths)}
            onChange={(v: any) => set('cushionMonths', Number(v))}
          />
          <Select2
            id="shortage-months"
            title="Collect Shortage Over"
            options={{ 12: '12 months', 24: '24 months', 36: '36 months' }}
            value={String(params.shortageMonths)}
            onChange={(v: any) => set('shortageMonths', Number(v))}
          />
          <Select2
            id="deficiency-months"
            title="Recover Deficiency Over"
            options={{ 2: '2 months', 6: '6 months', 12: '12 months' }}
            value={String(params.deficiencyMonths)}
            onChange={(v: any) => set('deficiencyMonths', Number(v))}
          />
        </div>
        <p className="text-[12px] text-gray-500 mt-1">
          Regulation X caps the cushion at one sixth of annual disbursements, and a surplus of $50 or
          more is refunded rather than credited.
        </p>

        {result && (
          <div className="mt-4 border-t pt-3">
            <div className="grid grid-cols-4 gap-3">
              <Figure label="Annual disbursements" value={result.annualDisbursements} />
              <Figure label="Base monthly" value={result.baseMonthly} />
              <Figure label="Cushion" value={result.cushion} />
              <Figure label={`Lowest point (${formatDate(result.lowestMonth)})`} value={result.lowestBalance} />
              <Figure label="Surplus" value={result.surplus} />
              <Figure label="Shortage" value={result.shortage} />
              <Figure label="Deficiency" value={result.deficiency} />
              <Figure label="New monthly escrow" value={result.newMonthlyEscrow} strong />
            </div>

            <p className="mt-3 text-[13px] text-gray-700">
              {result.explanation?.map((line: string, i: number) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </p>

            <p className="mt-3 font-medium">Projected account — {formatDate(result.computationYearStart)} onward</p>
            <div className="max-h-52 overflow-auto mt-1">
              <table className="w-full text-[13px]">
                <thead className="text-gray-600">
                  <tr>
                    <th className="text-left py-1">Month</th>
                    <th className="text-right py-1">Deposit</th>
                    <th className="text-right py-1">Paid Out</th>
                    <th className="text-left py-1 pl-4">For</th>
                    <th className="text-right py-1">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trialBalance?.map((m: any) => (
                    <tr key={m.month} className="border-t">
                      <td className="py-1">{formatDate(m.month)}</td>
                      <td className="py-1 text-right">{thousandSeperatorWithPrefix(m.deposit)}</td>
                      <td className="py-1 text-right">
                        {m.disbursed ? thousandSeperatorWithPrefix(m.disbursed) : ''}
                      </td>
                      <td className="py-1 pl-4 text-gray-500">
                        {m.disbursements?.map((d: any) => d.type).join(', ')}
                      </td>
                      <td className={`py-1 text-right ${m.balance < 0 ? 'text-red-600' : ''}`}>
                        {thousandSeperatorWithPrefix(m.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

const Figure = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
  <div>
    <p className="text-[12px] text-gray-500">{label}</p>
    <p className={strong ? 'font-semibold text-shade-blue' : ''}>{thousandSeperatorWithPrefix(Number(value) || 0)}</p>
  </div>
)
